import React, { useState, useEffect, useCallback } from 'react';
import './ManageStudentAssignments.css';
import '../TeacherDashboard/MyStudents.css';
import collegeConfig from '../../config/collegeConfig';
import { FaTags, FaSave, FaCheckCircle } from 'react-icons/fa';

const ManageBatchNames = () => {
  const token = localStorage.getItem('token');

  const [batches, setBatches] = useState([]); // distinct batch strings, e.g. "2023-2026"
  const [labels, setLabels] = useState({}); // { batch: label } as saved on the server
  const [drafts, setDrafts] = useState({}); // { batch: label } as currently being edited
  const [loading, setLoading] = useState(true);
  const [savingBatch, setSavingBatch] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [error, setError] = useState('');

  const showSuccess = (msg) => {
    setSaveSuccess(msg);
    setTimeout(() => setSaveSuccess(''), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [studentsRes, settingsRes] = await Promise.all([
        fetch(`${collegeConfig.api.baseURL}/students`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${collegeConfig.api.baseURL}/system-settings`)
      ]);

      const studentsData = await studentsRes.json();
      const settingsData = await settingsRes.json();

      if (studentsRes.ok) {
        const uniqueBatches = [...new Set(
          (studentsData.students || [])
            .flatMap((s) => s.assignedCourses?.map((c) => c.batch) || [])
            .filter(Boolean)
        )].sort();
        setBatches(uniqueBatches);
      } else {
        setError(studentsData.message || 'Failed to load batches');
      }

      if (settingsData.success) {
        const map = Object.fromEntries(
          (settingsData.batchLabels || []).map((b) => [b.batch, b.label])
        );
        setLabels(map);
        setDrafts(map);
      }
    } catch (err) {
      console.error('Error fetching batch names:', err);
      setError('Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveOne = async (batch) => {
    setSavingBatch(batch);
    setError('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/system-settings/batch-labels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ batch, label: drafts[batch] || '' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setLabels((prev) => ({ ...prev, [batch]: (drafts[batch] || '').trim() }));
        showSuccess('Saved');
      } else {
        setError(data.message || 'Failed to save batch name');
      }
    } catch (err) {
      console.error('Error saving batch name:', err);
      setError('Failed to save batch name');
    } finally {
      setSavingBatch(null);
    }
  };

  return (
    <div className="msa-container">
      <div className="msa-header">
        <h2><FaTags style={{ marginRight: 10 }} />Manage Batch Names</h2>
        <p className="msa-subtitle">
          Give any batch a short display name (e.g. name the 2023-2026 batch "R1"). The name will
          show on student cards in Monitor screens instead of the raw batch years. Leave a name
          blank and save to revert that batch back to its raw years.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {saveSuccess && <div className="ms-save-alert"><FaCheckCircle /> {saveSuccess}</div>}

      {loading ? (
        <div className="ms-loading">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      ) : batches.length === 0 ? (
        <div className="msa-empty-state">
          <p>No batches found yet — batches appear here once students are assigned to a course.</p>
        </div>
      ) : (
        <div className="ms-accordion">
          {batches.map((batch) => {
            const draft = drafts[batch] ?? '';
            const isDirty = draft !== (labels[batch] || '');
            return (
              <div key={batch} className="ms-accordion-card">
                <div className="ms-accordion-header" style={{ cursor: 'default' }}>
                  <div className="ms-teacher-info">
                    <div>
                      <span className="ms-teacher-name">{batch}</span>
                      <span className="ms-teacher-assigned">
                        {labels[batch] ? `Currently shown as "${labels[batch]}"` : 'No custom name set'}
                      </span>
                    </div>
                  </div>
                  <div className="ms-accordion-controls">
                    <input
                      type="text"
                      placeholder="e.g. R1"
                      value={draft}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [batch]: e.target.value }))}
                      style={{
                        padding: '8px 10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 13,
                        width: 140
                      }}
                    />
                    <button
                      className="ms-btn ms-btn-sm ms-btn-primary"
                      onClick={() => saveOne(batch)}
                      disabled={savingBatch === batch || !isDirty}
                    >
                      <FaSave />
                      {savingBatch === batch ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManageBatchNames;
